/**
 * L* Component Factory
 * Creates namespace-specific localization components with edit mode support
 * Creates LCommon, LPages, LAuth, LComponents via factory pattern
 * 
 * SIMPLIFIED 2-MODE ARCHITECTURE:
 * 1. Production: JSON → i18next fallback → defaultValue (crash safety)
 * 2. Edit Mode: DB values + inline editing (superadmin only)
 * 
 * SELF-HEALING:
 * When a key falls back to defaultValue (missing translation), it's reported
 * to the MissingKeyReporter for automatic translation.
 * 
 * Keys mode is a debug overlay (dev-only)
 */

import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalizationEdit } from '@/contexts/LocalizationEditContext';
import { useMissingKeyReporter } from '@/contexts/MissingKeyReporterContext';
import { InlineEditPopover } from './InlineEditPopover';
import { cn } from '@/lib/utils';
import { isKeysMode } from '@/i18n/config';

type Namespace = 'common' | 'pages' | 'auth' | 'components' | 'feedback';

interface LProps {
  id: string;
  values?: Record<string, any>;
  scope?: 'page' | 'header' | 'footer' | 'sidebar';
  className?: string;
  /** EN source of truth - used when i18n returns the key itself (missing/corrupted) */
  defaultValue?: string;
}

// Simple interpolation helper for edit mode (bypasses i18next)
const interpolate = (template: string, values?: Record<string, any>): string => {
  if (!values) return template;
  return Object.entries(values).reduce((result, [key, value]) => {
    return result.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), String(value ?? ''));
  }, template);
};

/**
 * Factory function to create namespace-specific localization components
 * Usage: const LCommon = createL('common');
 */
export function createL(namespace: Namespace) {
  return function LComponent({ id, values, scope = 'page', className, defaultValue }: LProps) {
    const compositeKey = `${namespace}.${id}`;
    
    // KEYS DEBUG MODE: Show raw translation keys (dev-only)
    if (isKeysMode()) {
      return (
        <span 
          className="font-mono text-[0.8em] leading-tight text-pink-500 bg-pink-50 dark:bg-pink-950/50 px-1 py-0.5 rounded"
          title={compositeKey}
        >
          [{compositeKey}]
        </span>
      );
    }
    
    const { t } = useTranslation(namespace);
    const { 
      isEditMode, 
      registerKey, 
      unregisterKey, 
      pendingEdits, 
      suggestions,
      getDbValue,
    } = useLocalizationEdit();
    
    // Self-healing: get reporter and check for self-healed translations
    const { reportMissingKey, translations: selfHealedTranslations } = useMissingKeyReporter();
    
    // Track if we've registered this key to prevent duplicate registrations
    const hasRegisteredRef = useRef(false);
    const lastRegisteredKeyRef = useRef<string | null>(null);
    
    // Track if we've reported this key for self-healing (to avoid duplicate reports)
    const hasReportedRef = useRef(false);
    
    // Check for self-healed translation first (highest priority after edit mode)
    const selfHealedValue = selfHealedTranslations.get(compositeKey);
    
    // Get i18next value (follows: locale JSON → fallback chain → returns key if nothing found)
    const i18nValue = String(t(id, values) || id);
    
    // In edit mode, prefer DB value over i18n value (with manual interpolation)
    const rawDbValue = isEditMode ? getDbValue(namespace, id) : undefined;
    const dbValue = rawDbValue ? interpolate(rawDbValue, values) : undefined;
    
    // Determine display value priority:
    // 1. Self-healed translation (from runtime self-heal)
    // 2. Edit mode with DB value → use DB value
    // 3. i18n returned the key itself (missing translation) → use defaultValue
    // 4. Otherwise → use i18n value
    const baseValue = selfHealedValue 
      ? interpolate(selfHealedValue, values)
      : (dbValue ?? i18nValue);
    
    const displayValue = (baseValue === id && defaultValue) 
      ? interpolate(defaultValue, values) 
      : baseValue;
    
    // For registration, use the pre-interpolation value
    const originalValue = rawDbValue ?? (i18nValue === id && defaultValue ? defaultValue : i18nValue);
    
    // Detect if we fell back to defaultValue (missing translation)
    const fellBackToDefault = !selfHealedValue && i18nValue === id && defaultValue;
    
    // Register key when in edit mode - use refs to prevent cascading re-renders
    useEffect(() => {
      if (isEditMode) {
        // Only register if key changed or we haven't registered yet
        if (!hasRegisteredRef.current || lastRegisteredKeyRef.current !== compositeKey) {
          registerKey(namespace, id, originalValue, scope);
          hasRegisteredRef.current = true;
          lastRegisteredKeyRef.current = compositeKey;
        }
        return () => {
          unregisterKey(namespace, id);
          hasRegisteredRef.current = false;
          lastRegisteredKeyRef.current = null;
        };
      } else {
        // Reset refs when exiting edit mode
        hasRegisteredRef.current = false;
        lastRegisteredKeyRef.current = null;
      }
    }, [isEditMode, compositeKey, id, originalValue, scope, registerKey, unregisterKey]);
    
    // Self-healing: Report missing key for automatic translation
    useEffect(() => {
      // Only report if we fell back to defaultValue and haven't reported yet
      if (fellBackToDefault && defaultValue && !hasReportedRef.current && !isEditMode) {
        hasReportedRef.current = true;
        reportMissingKey(namespace, id, defaultValue);
      }
      
      // Reset reported flag when key changes
      return () => {
        hasReportedRef.current = false;
      };
    }, [fellBackToDefault, defaultValue, namespace, id, isEditMode, reportMissingKey]);
    
    // Get final display value: pending edit > suggestion > computed value
    const finalDisplayValue = pendingEdits.get(compositeKey) 
      ?? suggestions.get(compositeKey)?.suggested 
      ?? displayValue;
    
    // Normal rendering when not in edit mode
    if (!isEditMode) return <>{finalDisplayValue}</>;
    
    // Edit mode: show highlighted, clickable text with inline edit popover
    const hasSuggestion = suggestions.has(compositeKey);
    const hasPendingEdit = pendingEdits.has(compositeKey);
    
    return (
      <InlineEditPopover
        namespace={namespace}
        localizationKey={id}
        currentValue={String(finalDisplayValue)}
        originalValue={originalValue}
      >
        <span 
          className={cn(
            "cursor-pointer rounded px-0.5 transition-colors",
            "hover:bg-yellow-100/70 hover:outline hover:outline-1 hover:outline-yellow-400/50",
            hasSuggestion && "bg-green-50 outline outline-1 outline-green-300",
            hasPendingEdit && "bg-blue-50 outline outline-1 outline-blue-300",
            className
          )}
          title={`${compositeKey} (⌘/Ctrl+Click to edit)`}
        >
          {finalDisplayValue}
        </span>
      </InlineEditPopover>
    );
  };
}

// Pre-created components for each namespace
export const LCommon = createL('common');
export const LPages = createL('pages');
export const LAuth = createL('auth');
export const LComponents = createL('components');
export const LFeedback = createL('feedback');

